import React from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import { BodySubtext } from '@/components/atomic/text'
import { TruncatedTextCell } from '@/components/atomic/truncated-text-cell'
import {
  formatNumber,
  isNullish,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'

interface DifferenceIndicatorProps {
  difference: number
  formatter?: (value: number) => string
  className?: string
  showText?: boolean
  invert?: boolean
}

function getStyles(difference: number, invert: boolean = false) {
  // Determine icon based on actual value movement
  // biome-ignore lint/suspicious/noImplicitAnyLet: Bulk suppression as part of biome migration
  let icon
  if (difference > 0) {
    icon = IconName.ArrowTrendingUpIcon
  } else if (difference < 0) {
    icon = IconName.ArrowTrendingDownIcon
  } else {
    icon = IconName.ArrowLongRightIcon
  }

  // Determine color based on value and invert setting
  // biome-ignore lint/suspicious/noImplicitAnyLet: Bulk suppression as part of biome migration
  let color
  const shouldShowGreen = invert ? difference < 0 : difference > 0

  if (difference === 0) {
    color = 'text-secondary'
  } else if (shouldShowGreen) {
    color = 'text-moment-green-800 dark:text-moment-green-400'
  } else {
    color = 'text-moment-red-800 dark:text-moment-red-400'
  }

  return { color, icon }
}

export const DifferenceIndicator = React.memo(function DifferenceIndicator({
  difference,
  formatter = (value) => formatNumber(value),
  className = '',
  showText = true,
  invert = false,
}: DifferenceIndicatorProps) {
  if (Number.isNaN(difference) || isNullish(difference)) {
    return PLACEHOLDER
  }
  const { color, icon } = getStyles(difference, invert)

  // Special handling for zero differences to make it more obvious
  if (difference === 0) {
    return (
      <div className={`flex items-center gap-1 ${color} ${className}`}>
        <Icon iconName={icon} className={`${color}`} />
        {showText && (
          <BodySubtext className="italic">
            <TruncatedTextCell
              text={`${formatter(difference)} (no change)`}
              maxLength={10}
            />
          </BodySubtext>
        )}
      </div>
    )
  }

  return (
    <div className={`flex gap-1 ${color} ${className}`}>
      <Icon iconName={icon} className={`${color}`} />
      {showText && <BodySubtext>{formatter(difference)}</BodySubtext>}
    </div>
  )
})
